import { Divider, Skeleton, Stack } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import Reports, { ChecklistReportType } from '../../../service/Reports';
import QueryFn from '../../../helpers/QueryFn';

type HistoricoChamado = {
  [id: string]: {
    comentario: string;
    data: string;
    dataMensagem: number;
    foto?: string;
    id: string;
    idChamado: string;
    nomeUsuarioAcompanhamento: string;
    status: string;
    usuarioAcompanhamento: string;
  };
};

type Ticket = {
  checklistId: number;
  cliente: string;
  conjunto: string;
  dataPrevisao: number;
  empresa: string;
  gerencia: string;
  id: string;
  inicio: number;
  instrucao: string;
  lastUpdateDate: number;
  local: string;
  modeloId: string;
  nomeChamado: string;
  nomeCliente: string;
  nomeConjunto: string;
  nomeGerencia: string;
  nomeLocal: string;
  nomeUnidade: string;
  nomeUsuario: string;
  novo: boolean;
  prioridade: string;
  status: string;
  unidade: string;
  usuario: string;
  itemId?: string;
  subitemId?: number;
  historicoChamado?: HistoricoChamado;
};

type Subitem = {
  nome: string;
  resposta: any;
  subitemId: number;
  tipo: string;
};

type TicketEntries = {
  [key: string]: Ticket[];
};

type TicketDetails = TicketEntries & {
  subtitens: Subitem[];
};

type RootObject = {
  ticketDetails: TicketDetails;
};

const reportsService = new Reports();
const queryFn = new QueryFn();

export default function ChamadosDetalhe({ chamadosArray, form, radioOption }: { chamadosArray: any, form: ChecklistReportType, radioOption: string }) {

  const ImageToShow = ({
    path,
    wid,
    hei,
    rotate,
    translate,
    skeletonWid,
    skeletonHei }: {
      path: string,
      wid: number,
      hei: number,
      rotate: number,
      translate: number,
      skeletonWid: number,
      skeletonHei: number
    }) => {
    const [image, setImage] = useState<string>('');
    const { mutate, data, isPending } = queryFn.useMutate('GET_IMAGES',
      async (path: string) => {
        const response = await reportsService.getImages([path]);
        return response;
      }
    );

    function resizeBase64Image(base64Image: string, wid: number, hei: number): Promise<string> {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = base64Image;
        img.onload = () => {
          let newWidth = wid;
          let newHeight = hei;

          const canvas = document.createElement('canvas');
          canvas.width = newHeight;
          canvas.height = newWidth;

          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.translate(0, translate);
            ctx.rotate(rotate);

            ctx.drawImage(img, 0, 0, newWidth, newHeight);

            const resizedBase64 = canvas.toDataURL('image/jpeg');
            resolve(resizedBase64);
            setImage(resizedBase64);
          } else {
            reject(new Error("Erro ao obter contexto 2D"));
          }
        };
        img.onerror = (err) => {
          console.error("Erro ao carregar a imagem: ", err);
          resolve('/sem_anexo.png');
        };
      });
    }

    useEffect(() => {
      mutate(path);
    }, []);

    useEffect(() => {
      resizeBase64Image(data, wid, hei);
    }, [isPending]);

    return (
      isPending
        ? <Skeleton variant='rounded' height={skeletonHei} width={skeletonWid} />
        : data &&
        <img style={{ objectFit: 'cover' }} src={image} alt="Imagem" />
    );
  };

  const formatStatus = (value: string) => {
    switch (value) {
      case 'pendente':
        return 'Pendente';
      case 'concluido':
        return 'Concluído';
      case 'emAndamento:':
      case 'emandamento':
        return 'Em Andamento';
      case 'recusado':
        return 'Recusado';
      case 'cancelado':
        return 'Cancelado';
    }
  };

  const ListaAcoes = ({ historico }: { historico: HistoricoChamado | undefined }) => {
    return (
      <>
        {historico
          && radioOption === 'all'
          && <> <Typography variant='h6'>Lista de Ações</Typography>
            <Card variant='outlined' sx={{ width: '100%' }}>
              {Object?.values(historico)?.map((chamado) => {
                return (
                  <div style={{ width: '100%' }} key={chamado.id}>
                    <Stack
                      width='100%'
                      style={{ padding: '20px' }}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={0.5}
                    >
                      <Stack
                        width='100%'
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={0.5}
                      >
                        <Stack
                          sx={{ fontSize: '14px' }}
                          direction="column"
                          justifyContent="flex-start"
                          alignItems="flex-start"
                          spacing={1}
                        >

                          <Typography variant='h5'>
                            {chamado?.nomeUsuarioAcompanhamento}
                          </Typography>
                          <span style={{ fontWeight: 600 }}>
                            Data de atendimento:&nbsp;
                            <span style={{ fontWeight: 400 }}>
                              {typeof chamado.data === 'number'
                                ? new Date(chamado.data).toLocaleString('pt-BR')
                                : chamado?.data || ''}
                            </span>
                          </span>
                          <span style={{ fontWeight: 600 }}>
                            Comentário:&nbsp;
                            <span style={{ fontWeight: 400 }}>
                              {chamado.comentario || '-'}
                            </span>
                          </span>
                          <span style={{ fontWeight: 600 }}>
                            Status:&nbsp;
                            <span style={{ fontWeight: 400 }}>
                              {formatStatus(chamado.status) || '-'}
                            </span>
                          </span>
                        </Stack>
                      </Stack>
                      {chamado.foto?.length
                        && <ImageToShow
                          path={chamado.foto as string}
                          wid={120}
                          hei={120}
                          rotate={0}
                          translate={0}
                          skeletonWid={120}
                          skeletonHei={120} />}
                    </Stack>
                    <span style={{ fontSize: '13px', padding: '10px' }}>
                      Atualizado em:&nbsp;{typeof chamado.dataMensagem === 'number'
                        ? new Date(chamado.dataMensagem)?.toLocaleString('pt-BR')
                        : chamado.dataMensagem || '-'}
                    </span>
                    <Divider variant="middle" />
                  </div>
                );
              })}
            </Card>
          </>
        }
      </>
    );
  };

  const DetailComp = ({ chamados }: { chamados: any }) => {
    return (
      chamados?.map((ticket: Ticket) => {
        return (
          <Stack
            key={ticket?.id}
            width='100%'
            sx={{ fontSize: '13px', color: 'gray' }}
            direction="column"
            justifyContent="flex-start"
            alignItems="flex-start"
            spacing={0.5}
          >
            <Typography variant='h6'>
              Informações do chamado:
            </Typography>
            <span style={{ fontWeight: 600 }}>
              Título:&nbsp;
              <span style={{ fontWeight: 400 }}>
                {ticket?.nomeChamado}
              </span>
            </span>
            <span style={{ fontWeight: 600 }}>
              Status:&nbsp;
              <span style={{ fontWeight: 400 }}>
                {formatStatus(ticket?.status)}
              </span>
            </span>
            <span style={{ fontWeight: 600 }}>
              Data limite:&nbsp;
              <span style={{ fontWeight: 400 }}>
                {typeof ticket.dataPrevisao === 'number'
                  ? new Date(ticket?.dataPrevisao).toLocaleString('pt-BR')
                  : ticket.dataPrevisao}
              </span>
            </span>
            <span style={{ fontWeight: 600 }}>
              Data de abertura:&nbsp;
              <span style={{ fontWeight: 400 }}>
                {typeof ticket.inicio === 'number'
                  ? new Date(ticket?.inicio).toLocaleString('pt-BR')
                  : ticket.inicio}
              </span>
            </span>
            <span style={{ fontWeight: 600 }}>
              Data da última mudança de status:&nbsp;
              <span style={{ fontWeight: 400 }}>
                {new Date(ticket?.lastUpdateDate)?.toLocaleString('pt-BR')}
              </span>
            </span>
            <span style={{ fontWeight: 600 }}>
              Usuário responsável pela última ação:&nbsp;
              <span style={{ fontWeight: 400 }}>
                {ticket?.nomeUsuario}
              </span>
            </span>
            <span style={{ fontWeight: 600 }}>
              Prioridade:&nbsp;
              <span style={{ fontWeight: 400 }}>
                {ticket?.prioridade?.replace(/^./, ticket?.prioridade[0].toUpperCase())}
              </span>
            </span>
            <ListaAcoes historico={ticket?.historicoChamado} />
          </Stack>
        );
      })
    );
  };

  const renderizaResposta = (subitem: Subitem, id: string) => {
    const itemId = id.slice(0, 1);
    const sub = form?.itens[itemId as any]?.subitens[subitem.subitemId];
    switch (subitem.tipo) {
      case 'assinatura':
        return (
          <ImageToShow
            path={subitem?.resposta}
            wid={100}
            hei={200}
            rotate={-Math.PI / 2}
            translate={100}
            skeletonWid={200}
            skeletonHei={100}
          />
        );
      case 'avaliacao':
        return (
          <>{(sub?.avaliacao as any[])[subitem?.resposta]?.nome}</>
        );
      case 'list':
        return (
          <Stack
            direction="column"
            spacing={0.5}
          >
            {(subitem.resposta as { texto: string }[]).map((ele) => {
              return (<li>{ele.texto}</li>);
            })}
          </Stack>
        );
      case 'multiple':
      case 'unique':
        return (
          <Stack
            direction="column"
            spacing={0.5}
          >
            {sub.selecao?.map((ele, index) => {
              if ((sub?.selecionados as boolean[])[index]) {
                return (<li>{ele}</li>);
              }
            })}
          </Stack>
        );
      case 'data':
        return (typeof subitem.resposta === 'number'
          ? new Date(subitem.resposta).toLocaleString('pt-BR')
          : subitem.resposta);
      default: return (subitem.resposta);
    }
  };

  return (
    <Card sx={{ width: '100%' }}>
      <CardContent>
        <Typography variant='subtitle1' sx={{ color: 'gray' }}>
          CHAMADOS
        </Typography>
        <Typography>
          {form.nome}
        </Typography>
        <DetailComp chamados={chamadosArray?.ticketDetails?.formulario} />
        <Typography>
          {Object.keys(chamadosArray?.ticketDetails?.subitens)?.length > 0 &&
            <>
              <Typography variant='subtitle1' sx={{ marginTop: '15px', color: 'gray' }}>
                SUBITENS
              </Typography>
              {Object?.keys(chamadosArray?.ticketDetails?.subitens)
                ?.sort((a, b) => a?.toLowerCase() > b?.toLowerCase() ? 1 : -1)
                ?.map((id: string) => (
                  <>
                    <Stack
                      width='100%'
                      direction="column"
                      justifyContent="flex-start"
                      alignItems="flex-start"
                      spacing={0.5}
                    >
                      <span>
                        {`${id.replace('|', ' . ')} - ${chamadosArray?.ticketDetails?.subitens[id]?.nomeItem}: ${chamadosArray?.ticketDetails?.subitens[id]?.nome}`}
                      </span>
                      <Stack
                        width='100%'
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={0.5}
                      >
                        Pergunta:&nbsp;&nbsp;{renderizaResposta(chamadosArray?.ticketDetails?.subitens[id], id)}
                      </Stack>
                      <DetailComp chamados={chamadosArray?.ticketDetails?.subitens[id].chamados} />
                    </Stack>
                    <Divider variant='middle' />
                  </>
                ))}
            </>
          }
        </Typography>
      </CardContent>
    </Card >
  );
}